import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Card,
  FormControlLabel,
  Grid,
  Switch,
  TextField
} from '@material-ui/core';
import { BackdropCircular } from 'components';
import {
  getBulkPriceTargetsAPI,
  validateBulkPriceCsvAPI,
  startBulkPriceRunAPI,
  getBulkPriceRunAPI,
  exportBulkPriceRunAPI
} from 'api';
import { receiveBulkPriceProgress, resetBulkPrice } from 'redux/actions/bulk_price';
import { SocketContext } from 'contexts/SocketContext';
import UploadZone from 'pages/customers/customersImport/UploadZone';
import DownloadTemplateButton from './DownloadTemplateButton';
import StoresMultiSelect from './StoresMultiSelect';
import CsvValidation from './CsvValidation';
import ConfirmRunDialog from './ConfirmRunDialog';
import RunProgress from './RunProgress';
import ResultsTable from './ResultsTable';


const POLL_INTERVAL_MS = 5000;
const FINISHED_STATUSES = ['COMPLETED', 'FAILED'];

const BulkPriceUpdate = () => {
  const dispatch = useDispatch();
  const socket = useContext(SocketContext);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Kept in a ref so the poll always asks for the page the operator is actually looking at,
  // rather than the one captured when the interval was created.
  const pageRef = useRef(1);

  const [fileData, setFileData] = useState({ headers: [], data: [], name: '' });
  const [headersError, setHeadersError] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [brand, setBrand] = useState('');
  const [isReindexEnabled, setIsReindexEnabled] = useState(true);
  const [stage, setStage] = useState('upload');
  const [pendingDryRun, setPendingDryRun] = useState(true);

  const {
    targets,
    loadingTargets,
    validation,
    loadingValidation,
    runId,
    run,
    results,
    resultsPage,
    resultsTotalCount,
    startingRun,
    exportingRun,
    progressByTarget,
    liveCounters
  } = useSelector((state: IReduxState) => state.bulk_price);

  const { data, headers, name } = fileData;
  const itemCount = validation?.validCount || 0;
  const isRunActive = Boolean(run) && !FINISHED_STATUSES.includes(run.status);

  useEffect(() => {
    getBulkPriceTargetsAPI({ dispatch });

    return () => {
      dispatch(resetBulkPrice());
    };
  }, []);

  /**
   * Progress arrives on a run scoped channel, and the poll is the belt to that braces:
   * PrivateRoute calls a bare socket.off() before subscribing to the active store channel,
   * which drops every listener including this one, and a page reload has no listener at all.
   */
  useEffect(() => {
    if (!runId) {
      return undefined;
    }

    const channel = `bulk-price:${runId}`;
    const handleProgress = (payload) => {
      dispatch(receiveBulkPriceProgress(payload));
    };

    pageRef.current = 1;

    socket.on(channel, handleProgress);
    getBulkPriceRunAPI({ dispatch, runId, page: 1 });

    pollRef.current = setInterval(() => {
      getBulkPriceRunAPI({ dispatch, runId, page: pageRef.current, silent: true });
    }, POLL_INTERVAL_MS);

    return () => {
      socket.off(channel, handleProgress);
      clearInterval(pollRef.current);
    };
  }, [runId, socket]);

  /**
   * Stop polling once the run is finished, but fetch once more before doing so.
   *
   * The finish frame carries the status and nothing else, and the last chunk's results are
   * written milliseconds before it is emitted - so the poll that would have picked them up is
   * cancelled by the very event that says they exist. Without this last read the results table
   * stays empty and the export button stays disabled on a run that succeeded.
   *
   * Safe to run from an effect keyed on the status: the refetch cannot change a terminal status,
   * so it cannot re-enter, and pollRef is nulled first either way.
   */
  useEffect(() => {
    if (run && FINISHED_STATUSES.includes(run.status) && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
      getBulkPriceRunAPI({ dispatch, runId, page: pageRef.current, silent: true });
    }
  }, [run?.status]);

  const resetFileData = () => {
    setFileData({ headers: [], data: [], name: '' });
    setHeadersError(false);
    setStage('upload');
    dispatch(resetBulkPrice());
    getBulkPriceTargetsAPI({ dispatch });
  };

  const handleValidate = async () => {
    // Rows are mapped positionally by the API - col 0 SKU, col 1 price - so the header row that
    // UploadZone already stripped never matters.
    const result = await validateBulkPriceCsvAPI({ dispatch, rows: data });

    if (result) {
      setStage('validate');
    }
  };

  const handleProceedToOptions = () => {
    setStage('options');
  };

  const handleSelectAll = () => {
    setSelectedTargets(targets.filter((target) => target.eligible));
  };

  const handleAskPreview = () => {
    setPendingDryRun(true);
    setStage('confirm');
  };

  const handleAskRun = () => {
    setPendingDryRun(false);
    setStage('confirm');
  };

  const handleCancelConfirm = () => {
    setStage('options');
  };

  const handleConfirmRun = async () => {
    const started = await startBulkPriceRunAPI({
      dispatch,
      rows: data,
      targetIds: selectedTargets.map((target) => target.id),
      brand,
      reindexAfterEachStore: isReindexEnabled,
      dryRun: pendingDryRun
    });

    setStage(started ? 'run' : 'options');
  };

  const handleApplyForReal = () => {
    setPendingDryRun(false);
    setStage('confirm');
  };

  const handleExport = () => {
    exportBulkPriceRunAPI({ dispatch, runId });
  };

  const handlePageChange = (page: number) => {
    pageRef.current = page;
    getBulkPriceRunAPI({ dispatch, runId, page });
  };

  // Rendered inside the admin page's tab panel, which already owns the page padding, the
  // scroll container and the heading - hence no breadcrumb and no horizontal margin here.
  return (
    <div className="flex-grow flex-column relative pb-5">
      <Card elevation={0} className="p-8 overflow-unset text-center">
        {stage === 'upload' && !headers.length &&
          <>
            <div className="text-left mb-4 text-13 text-muted">
              Upload a CSV of SKUs and new prices. The first column is the SKU and the second is
              the price, whatever the header says. Prices can only be raised: a price below the
              current one is reported and skipped. Every change is written through the Magento
              API, so it lands in the store log.
            </div>
            <UploadZone
              setFileData={setFileData}
              setHeadersError={setHeadersError}
              isDisabled={false}
            />
            <DownloadTemplateButton />
          </>
        }

        {Boolean(headers.length) &&
          <div className="text-left text-13 text-muted mb-2">
            <b>{name}</b> &middot; {data.length} row(s) read
          </div>
        }

        {stage === 'options' &&
          <Grid container spacing={3} className="text-left">
            <Grid item xs={12}>
              <StoresMultiSelect
                targets={targets}
                selected={selectedTargets}
                isDisabled={isRunActive}
                setSelected={setSelectedTargets}
                onSelectAll={handleSelectAll}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Brand filter (optional)"
                value={brand}
                helperText="Leave empty to update every SKU in the file. Matched against the product name, case insensitive."
                onChange={(event) => setBrand(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={isReindexEnabled}
                    onChange={(event) => setIsReindexEnabled(event.target.checked)}
                  />
                }
                label="Reindex and flush cache after each store"
              />
              <div className="text-13 text-muted">
                Leave this on unless DevOps will reindex afterwards. With it off the storefront
                keeps serving the old prices until someone does.
              </div>
            </Grid>
            <Grid item xs={12} className="flex justify-center">
              <Button variant="outlined" className="uppercase mr-2" onClick={resetFileData}>
                Start over
              </Button>
              <Button
                color="primary"
                variant="outlined"
                className="uppercase mr-2"
                disabled={!selectedTargets.length || !itemCount}
                onClick={handleAskPreview}
              >
                Preview
              </Button>
              <Button
                color="primary"
                variant="contained"
                className="uppercase"
                disabled={!selectedTargets.length || !itemCount}
                onClick={handleAskRun}
              >
                Skip preview and apply
              </Button>
            </Grid>
          </Grid>
        }

        {(stage === 'run' || stage === 'confirm') && Boolean(run) &&
          <>
            <RunProgress
              run={run}
              progressByTarget={progressByTarget}
              liveCounters={liveCounters}
            />
            <ResultsTable
              results={results}
              page={resultsPage}
              limit={50}
              totalCount={resultsTotalCount}
              onPageChange={handlePageChange}
            />
            <div className="flex justify-center mt-4">
              <Button variant="outlined" className="uppercase mr-2" onClick={resetFileData}>
                Start over
              </Button>
              <Button
                variant="outlined"
                className="uppercase mr-2"
                disabled={exportingRun || !resultsTotalCount}
                onClick={handleExport}
              >
                Download results CSV
              </Button>
              {run.dryRun && FINISHED_STATUSES.includes(run.status) &&
                <Button
                  color="primary"
                  variant="contained"
                  className="uppercase"
                  onClick={handleApplyForReal}
                >
                  Apply for real
                </Button>
              }
            </div>
          </>
        }
      </Card>

      {stage === 'validate' && Boolean(validation) &&
        <CsvValidation
          validation={validation}
          fileName={name}
          onReset={resetFileData}
          onProceed={handleProceedToOptions}
        />
      }

      {stage === 'upload' && Boolean(headers.length) &&
        <div className="flex justify-center p-5">
          <Button
            color="primary"
            variant="contained"
            className="uppercase"
            disabled={!data.length || headersError}
            onClick={handleValidate}
          >
            Validate Data
          </Button>
        </div>
      }

      <ConfirmRunDialog
        isOpen={stage === 'confirm'}
        title={pendingDryRun ? 'Run a preview?' : 'Apply these prices?'}
        storeCount={selectedTargets.length}
        itemCount={itemCount}
        brand={brand}
        isDryRun={pendingDryRun}
        onCancel={handleCancelConfirm}
        onConfirm={handleConfirmRun}
      />

      <BackdropCircular loading={loadingTargets || loadingValidation || startingRun} />
    </div>
  );
};

export default BulkPriceUpdate;
